import { useEffect, useState } from 'react';
import { slugify, type Adventure, type Instance, type InstancePlayer, type Person } from '@gm-binder/shared';
import { api } from '../../api/client';
import { Modal, Button, Select, Input } from '../ui';
import { useRunStore } from '../../store/runStore';
import { useUiStore } from '../../store/uiStore';
import styles from './SessionTools.module.css';

/** Link each PG (from the adventure) to a person in the GM roster. */
export function RosterModal({
  instance,
  adventure,
  onClose,
}: {
  instance: Instance;
  adventure: Adventure;
  onClose: () => void;
}) {
  const [people, setPeople] = useState<Person[]>([]);
  const [links, setLinks] = useState<Record<string, string>>({});
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const setInstance = useRunStore((s) => s.setInstance);
  const toast = useUiStore((s) => s.toast);

  useEffect(() => {
    void (async () => {
      try {
        setPeople(await api.listPeople());
      } catch {
        setPeople([]);
      }
    })();
  }, []);

  useEffect(() => {
    const init: Record<string, string> = {};
    instance.players.forEach((p) => {
      if (p.personId) init[p.characterId ?? slugify(p.characterName)] = p.personId;
    });
    setLinks(init);
  }, [instance]);

  const addPerson = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const p = await api.createPerson({ name });
      setPeople((ps) => [...ps, p].sort((a, b) => a.name.localeCompare(b.name)));
      setNewName('');
      toast('Persona aggiunta', 'success');
    } catch {
      toast('Creazione fallita', 'danger');
    }
  };

  const save = async () => {
    const players: InstancePlayer[] = adventure.characters
      .filter((c) => links[c.id ?? slugify(c.name)])
      .map((c) => ({
        personId: links[c.id ?? slugify(c.name)],
        characterName: c.name,
        characterId: c.id ?? slugify(c.name),
      }));
    setSaving(true);
    try {
      const updated = await api.setInstancePlayers(instance.id, players);
      setInstance(updated);
      toast('Giocatori aggiornati', 'success');
      onClose();
    } catch {
      toast('Salvataggio fallito', 'danger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="👥 Giocatori e personaggi" width={560}>
      <div className={styles.stack}>
        <p className={styles.hint}>
          Collega ogni personaggio (PG) al giocatore che lo interpreta. Questi collegamenti
          alimentano la pianificazione e i presenti delle sessioni.
        </p>

        <div className={styles.section}>
          {adventure.characters.length === 0 && (
            <p className={styles.hint}>Questa avventura non ha ancora personaggi.</p>
          )}
          {adventure.characters.map((c) => (
            <div key={c.id ?? slugify(c.name)} className={styles.rosterRow}>
              <span className={styles.rosterName}>{c.name}</span>
              <Select
                value={links[c.id ?? slugify(c.name)] ?? ''}
                onChange={(e) =>
                  setLinks((l) => ({ ...l, [c.id ?? slugify(c.name)]: e.target.value }))
                }
              >
                <option value="">— Nessuno —</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </div>
          ))}
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Aggiungi una persona</h3>
          <div className={styles.linkRow}>
            <Input
              style={{ flex: 1 }}
              value={newName}
              placeholder="Nome del giocatore"
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void addPerson();
                }
              }}
            />
            <Button variant="default" onClick={() => void addPerson()} disabled={!newName.trim()}>
              Aggiungi
            </Button>
          </div>
        </div>

        <div className={styles.actions}>
          <span />
          <div className={styles.actionsRight}>
            <Button variant="ghost" onClick={onClose}>
              Annulla
            </Button>
            <Button variant="primary" onClick={() => void save()} disabled={saving}>
              Salva
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
